import * as React from 'react';

import { useFormContext } from 'react-hook-form';

import {
  Box,
  Collapse,
  Divider,
  Grid,
} from '@mui/material';

import {
  FlightOperationNotification,
  FormGenerator,
  RadioGroupOption,
} from '../../../core/api/clients';

import ControlledCascadingSelect from '../../../components/ControlledCascadingSelect';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledTimeField from '../../../components/ControlledTimeField';
import ControlledDatePicker from '../../../components/ControlledDatePicker';
import ControlledRadioGroup from '../../../components/ControlledRadioGroup';
import ControlledSelect from '../../../components/ControlledSelect';

import { useFlightOperationNotificationFieldOptions } from '../../../core/context/FlightOperationNortificationFieldOptionContext';
import { useFlightOperationNotificationData } from '../../../core/context/FlightOperationNotificationDataContext';

interface EventStepProps {
  visible: boolean;
  readOnly?: boolean;
  isPriorVersion?: boolean;
}

function EventStep({ visible, readOnly, isPriorVersion }: EventStepProps) {
  const { eventOptions, phaseOptions } = useFlightOperationNotificationFieldOptions();
  const { control, setValue, resetField } = useFormContext<FlightOperationNotification>();
  const { flightOperationNotification, allowedActions } = useFlightOperationNotificationData();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex">
            <ControlledTextField<FlightOperationNotification>
              name="eventTitle"
              control={control}
              rules={{ required: true }}
              readOnly={readOnly}
              disableEditPopper={!allowedActions?.edit || isPriorVersion}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextField<FlightOperationNotification>
            name="location"
            label="Location of Event"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledDatePicker<FlightOperationNotification>
            name="eventDate"
            control={control}
            readOnly={readOnly || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTimeField
            name="localEventTime"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<FlightOperationNotification>
            groupName="eventType"
            name="eventType"
            control={control}
            fieldNames={['eventType', 'eventType2', 'eventType3']}
            options={eventOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ControlledSelect<FlightOperationNotification>
            name="typeOfFlight"
            label="Type of Flight"
            control={control}
            menuItems={[
              { name: 'Revenue (part 121)', value: 'Revenue (part 121)' },
              { name: 'Non-Revenue (part 91)', value: 'Non-Revenue (part 91)' },
              { name: 'Maintenance Test', value: 'Maintenance Test' },
              { name: 'Special Flight Permit', value: 'Special Flight Permit' },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextField<FlightOperationNotification>
            name="numberOfPassengers"
            label="Number of Passengers"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
            TextFieldProps={{
              inputProps: {
                onBlur: (event) => {
                  const { value } = event.target;
                  const nan = Number.isNaN(Number(value));
                  if (value === '') {
                    setValue('numberOfPassengers', null);
                  } else if (nan) {
                    resetField('numberOfPassengers', {
                      keepDirty: false,
                      keepError: false,
                      keepTouched: false,
                    });
                  }
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={8} md={4} lg={5} xl={4}>
          <ControlledRadioGroup<FlightOperationNotification>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Declared"
            name="emergencyDeclared"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
              { label: 'Unknown', value: RadioGroupOption.Unknown },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<FlightOperationNotification>
            groupName="phaseOfFlight"
            name="phaseOfFlight"
            control={control}
            fieldNames={['phaseOfFlight', 'phaseOfFlight2']}
            options={phaseOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default EventStep;
